/**
 * Formatting utilities for AI Assistant
 */

import { t, getLanguage } from '../../../i18n.js';

function dateLocale() {
  return getLanguage() === 'de' ? 'de-DE' : 'en-US';
}

/**
 * Format a date as relative time (e.g., "2 hours ago", "Yesterday")
 * @param {string|Date} date - ISO date string or Date object
 * @returns {string}
 */
export function formatRelativeTime(date) {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now - then;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffSecs < 60) {
    return t('just_now');
  } else if (diffMins < 60) {
    return t('minutes_ago', { n: String(diffMins) });
  } else if (diffHours < 24) {
    return t('hours_ago', { n: String(diffHours) });
  } else if (diffDays === 1) {
    return t('yesterday');
  } else if (diffDays < 7) {
    return t('days_ago', { n: String(diffDays) });
  } else {
    return then.toLocaleDateString(dateLocale(), { 
      month: 'short', 
      day: 'numeric' 
    });
  }
}

/**
 * Format a date for grouping (Today, Yesterday, This Week, etc.)
 * @param {string|Date} date - ISO date string or Date object
 * @returns {string}
 */
export function formatDateGroup(date) {
  const now = new Date();
  const then = new Date(date);
  
  // Reset times to compare dates only
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const thenDate = new Date(then.getFullYear(), then.getMonth(), then.getDate());
  
  const diffDays = Math.floor((today - thenDate) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return t('today');
  } else if (diffDays === 1) {
    return t('yesterday');
  } else if (diffDays < 7) {
    return t('this_week');
  } else if (diffDays < 30) {
    return t('this_month');
  } else {
    return then.toLocaleDateString(dateLocale(), { 
      month: 'long', 
      year: 'numeric' 
    });
  }
}

/**
 * Truncate text to a maximum length
 * @param {string} text 
 * @param {number} maxLength 
 * @returns {string}
 */
export function truncateText(text, maxLength = 50) {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
}

/**
 * Format message count
 * @param {number} count 
 * @returns {string}
 */
export function formatMessageCount(count) {
  if (count === 0) return t('no_messages');
  if (count === 1) return t('one_message');
  return t('n_messages', { n: String(count) });
}

/**
 * Group conversations by date
 * @param {Array} conversations 
 * @returns {Map<string, Array>}
 */
export function groupConversationsByDate(conversations) {
  const groups = new Map();
  
  conversations.forEach(conversation => {
    const dateKey = formatDateGroup(conversation.lastMessageAt || conversation.createdAt);
    
    if (!groups.has(dateKey)) {
      groups.set(dateKey, []);
    }
    groups.get(dateKey).push(conversation);
  });
  
  return groups;
}
